// How the monitor writes a figure. Money, tokens and clock times in one place, so a number
// never appears in two shapes on the same page.

import { dayMonth } from '../day.js';

/**
 * Dollars. Two places above a dollar, because that is how a bill reads; four below it, because
 * a lane that costs a third of a cent a call is a fact worth seeing, and $0.00 is not.
 */
export function usd(v: number): string {
  if (!Number.isFinite(v)) return '—';
  if (v === 0) return '$0';
  if (Math.abs(v) < 0.01) return `$${v.toFixed(4)}`;
  if (Math.abs(v) < 1000) return `$${v.toFixed(2)}`;
  return `$${Math.round(v).toLocaleString('en-GB')}`;
}

/** Tokens: 52,362,969 reads as 52.4M. The exact figure goes in the title attribute. */
export function tokens(v: number): string {
  if (!Number.isFinite(v) || v === 0) return '0';
  if (v < 1000) return String(v);
  if (v < 1_000_000) return `${(v / 1000).toFixed(v < 10_000 ? 1 : 0)}k`;
  return `${(v / 1_000_000).toFixed(v < 10_000_000 ? 1 : 0)}M`;
}

export const exact = (v: number): string => v.toLocaleString('en-GB');

/** The reader's own clock, not UTC: the store is UTC and the page converts. */
export function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function dayLabel(day: string): string {
  return dayMonth(new Date(`${day}T12:00:00Z`));
}

/** "2 hours ago", "just now" — how stale the figures are, said plainly. */
export function since(iso: string | null, nowMs: number): string {
  if (iso === null) return 'never';
  const secs = Math.max(0, Math.round((nowMs - Date.parse(iso)) / 1000));
  if (secs < 45) return 'just now';
  if (secs < 5400) return `${Math.round(secs / 60)} min ago`;
  const hours = Math.round(secs / 3600);
  return hours < 36 ? `${hours} hr ago` : `${Math.round(hours / 24)} days ago`;
}

/** "1 call", "52 calls" — because "1 calls" on a page about money reads as a bug, and is one. */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${exact(n)} ${n === 1 ? one : many}`;
}
